<template lang="pug">
section.cte-list
  .action-bar
    el-button(type="primary" icon="el-icon-download" @click="exportDialogVisible = true") Exportar XMLs

  snowflake-search-list(
    context="cte",
    class="cte-search-result",
    :service="cteService",
    :action="false",
    :details="false",
    :operations="false",
    :handle-row-selection="handleCteSelection",
    :pre-search="true",
    :page-size="20"
  )
    template(slot="tableRows")
      el-table-column(prop="accessKey" label="Chave de acesso" min-width="320")
        template(slot-scope="scope")
          code.access-key {{ scope.row.accessKey }}

      el-table-column(prop="issuedAt" label="Emitido em" width="180" sortable)
        template(slot-scope="scope")
          | {{ formatDate(scope.row.issuedAt) }}

      el-table-column(prop="issuerCnpj" label="Emitente (CNPJ)" width="160")
        template(slot-scope="scope")
          .issuer
            div {{ scope.row.issuerName || '—' }}
            small.cnpj {{ formatCnpj(scope.row.issuerCnpj) }}

      el-table-column(prop="totalValue" label="Valor" width="120" align="right")
        template(slot-scope="scope")
          | {{ formatCurrency(scope.row.totalValue) }}

      el-table-column(label="Status" width="140")
        template(slot-scope="scope")
          el-tag(v-if="scope.row.cancelledAt" type="danger" size="small") Cancelado
          el-tag(v-else type="success" size="small") Autorizado

    template(slot="emptyText")
      span(v-if="hasShortQuery") Digite ao menos 6 dígitos da chave de acesso ou do CNPJ emitente.
      span(v-else) Nenhum CT-e encontrado.

  el-dialog(
    title="Exportar CT-es"
    :visible.sync="exportDialogVisible"
    width="480px"
    :close-on-click-modal="!exporting"
    :close-on-press-escape="!exporting"
    :show-close="!exporting"
    @closed="onDialogClosed"
  )
    el-form(label-width="60px")
      el-form-item(label="De")
        el-date-picker(
          v-model="exportFrom",
          type="date",
          value-format="yyyy-MM-dd",
          placeholder="Data inicial",
          :picker-options="fromPickerOptions",
          style="width: 100%"
        )
      el-form-item(label="Até")
        el-date-picker(
          v-model="exportTo",
          type="date",
          value-format="yyyy-MM-dd",
          placeholder="Data final",
          :picker-options="toPickerOptions",
          style="width: 100%"
        )
    small.export-error(v-if="exportRangeError") {{ exportRangeError }}
    small.export-hint(v-else) Intervalo máximo: 2 meses. Inclui CT-es e seus eventos.
    span(slot="footer")
      el-button(@click="exportDialogVisible = false" :disabled="exporting") Cancelar
      el-button(
        type="primary",
        icon="el-icon-download",
        :loading="exporting",
        :disabled="!canExport",
        @click="onExport"
      ) Exportar ZIP
</template>

<script>
import moment from 'moment';

import CteService from '@/services/fulfillment/CteService';
import SnowflakeSearchList from '@/components/list/SearchList.vue';

export default {
  name: 'cte-list',
  components: { SnowflakeSearchList },
  data() {
    return {
      cteService: CteService,
      exportFrom: null,
      exportTo: null,
      exporting: false,
      exportDialogVisible: false,
    };
  },
  computed: {
    hasShortQuery() {
      const q = this.$route.query.search || '';
      const digits = q.replace(/\D/g, '');
      return q.length > 0 && digits.length < 6;
    },
    exportRangeError() {
      if (!this.exportFrom || !this.exportTo) return null;

      const from = moment(this.exportFrom, 'YYYY-MM-DD');
      const to = moment(this.exportTo, 'YYYY-MM-DD');
      if (to.isBefore(from, 'day')) {
        return 'A data final deve ser igual ou posterior à inicial.';
      }

      if (to.diff(from, 'days') > 61) {
        return 'O intervalo não pode exceder 2 meses (62 dias).';
      }
      return null;
    },
    canExport() {
      return !!this.exportFrom && !!this.exportTo && this.exportRangeError === null;
    },
    fromPickerOptions() {
      const to = this.exportTo;
      return {
        disabledDate(time) {
          if (!to) return false;
          const toMoment = moment(to, 'YYYY-MM-DD');
          const t = moment(time);
          return t.isAfter(toMoment, 'day') || toMoment.diff(t, 'days') > 61;
        },
      };
    },
    toPickerOptions() {
      const from = this.exportFrom;
      return {
        disabledDate(time) {
          if (!from) return false;
          const fromMoment = moment(from, 'YYYY-MM-DD');
          const t = moment(time);
          return t.isBefore(fromMoment, 'day') || t.diff(fromMoment, 'days') > 61;
        },
      };
    },
  },
  methods: {
    formatDate(iso) {
      if (!iso) return '—';
      return moment(iso).format('DD/MM/YYYY HH:mm');
    },
    formatCnpj(cnpj) {
      if (!cnpj || cnpj.length !== 14) return cnpj || '';
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
    },
    formatCurrency(value) {
      if (value == null) return '—';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
    async onExport() {
      if (!this.exportFrom || !this.exportTo) {
        this.$message.warning('Selecione a data inicial e final.');
        return;
      }

      if (this.exportRangeError) {
        this.$message.warning(this.exportRangeError);
        return;
      }

      this.exporting = true;
      try {
        await CteService.downloadExport(this.exportFrom, this.exportTo);
        this.exportDialogVisible = false;
      } catch (e) {
        this.$message.error(e.message || 'Falha ao exportar.');
      } finally {
        this.exporting = false;
      }
    },
    onDialogClosed() {
      this.exportFrom = null;
      this.exportTo = null;
    },
    handleCteSelection(row) {
      if (row && row.accessKey) {
        this.$router.push({ name: 'cte-detail', params: { accessKey: row.accessKey } });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cte-list {
  .action-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  }

  .access-key {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }

  .issuer {
    .cnpj {
      color: #909399;
      font-size: 12px;
    }
  }

  .export-hint {
    color: #909399;
    display: block;
    margin-top: -8px;
  }

  .export-error {
    color: #f56c6c;
    display: block;
    margin-top: -8px;
  }
}

.cte-search-result::v-deep(.clickable) {
  cursor: pointer;
}
</style>
